import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/common/DataTable";
import { campaignService, commTemplateService, segmentService, channelService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Send, CheckCircle2, XCircle, CalendarClock, PlayCircle,
  Users, ShieldCheck, AlertTriangle, FileText, Pause, Ban,
} from "lucide-react";
import { CAMPAIGN_STATUS_COLOR, CHANNEL_COLOR, RESPONSE_COLOR } from "./constants";

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        {Icon && <Icon className="h-3.5 w-3.5" />} {title}
      </h3>
      {children}
    </div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [channels, setChannels] = useState([]);
  const [audience, setAudience] = useState(null);
  const [responses, setResponses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("build");
  const [busy, setBusy] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  const canEdit = hasPermission("CAMPAIGN.EDIT");
  const canApprove = hasPermission("CAMPAIGN.APPROVE");
  const canExecute = hasPermission("CAMPAIGN.EXECUTE");

  const load = useCallback(async () => {
    try {
      const data = await campaignService.get(id);
      setC(data);
      const tid = data.tenantId;
      const [t, s, ch, aud] = await Promise.all([
        commTemplateService.list({ tenantId: tid, size: 200 }),
        segmentService.list({ tenantId: tid, size: 200 }),
        channelService.list({ tenantId: tid }),
        campaignService.audience(id),
      ]);
      setTemplates(t.items || []); setSegments(s.items || []);
      setChannels((ch.items || []).filter((x) => x.providerType === "whatsapp"));
      setAudience(aud);
      if (data.status === "Completed" || data.status === "Running") {
        const [r, a] = await Promise.all([campaignService.responses(id, { size: 100 }), campaignService.analytics(id)]);
        setResponses(r.items || []); setAnalytics(a);
      }
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveField = (changes) => act(() => campaignService.update(id, changes), "Saved");
  const toggleSegment = (sid) => {
    const cur = c.segmentIds || [];
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    act(() => campaignService.setSegments(id, { segmentIds: next }));
  };
  const refreshAudience = () => act(async () => { setAudience(await campaignService.audience(id)); });

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!c) return null;

  const editable = ["Draft", "Rejected"].includes(c.status);
  const validTemplates = templates.filter((t) =>
    c.channel === "whatsapp" ? (t.approvalStatus === "approved" && ["whatsapp", "any"].includes(t.channelType))
      : (t.status === "Active" || true));

  return (
    <div data-testid="campaign-detail-page">
      <button onClick={() => navigate("/admin/campaigns/list")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="campaign-back">
        <ArrowLeft className="h-4 w-4" /> Back to campaigns
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge color={CHANNEL_COLOR[c.channel]}>{c.channel}</Badge>
            <Badge color={CAMPAIGN_STATUS_COLOR[c.status]}>{c.status}</Badge>
            {c.approval?.rejectedReason && c.status === "Rejected" && <span className="text-xs text-red-600">{c.approval.rejectedReason}</span>}
          </div>
          <h1 className="text-2xl font-bold text-foreground">{c.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{c.objective} · {c.description || "No description"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="campaign-actions">
          {editable && canEdit && (
            <button onClick={() => act(() => campaignService.submit(id), "Submitted for approval")} disabled={busy || !c.templateId || (audience && audience.eligible === 0)}
              title={!c.templateId ? "Select a template first" : (audience && audience.eligible === 0 ? "No consent-eligible recipients — adjust your audience segments" : "")}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="campaign-submit-btn">
              <Send className="h-4 w-4" /> Submit for approval
            </button>
          )}
          {c.status === "Pending Approval" && canApprove && (
            <>
              <button onClick={() => act(() => campaignService.approve(id), "Approved")} disabled={busy}
                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60" data-testid="campaign-approve-btn">
                <CheckCircle2 className="h-4 w-4" /> Approve
              </button>
              <button onClick={() => { const r = window.prompt("Rejection reason?"); if (r !== null) act(() => campaignService.reject(id, { reason: r }), "Rejected"); }} disabled={busy}
                className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-60" data-testid="campaign-reject-btn">
                <XCircle className="h-4 w-4" /> Reject
              </button>
            </>
          )}
          {c.status === "Approved" && canExecute && (
            <button onClick={() => act(() => campaignService.execute(id), "Campaign executed")} disabled={busy}
              className="inline-flex items-center gap-1.5 bg-teal-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-teal-700 disabled:opacity-60" data-testid="campaign-execute-btn">
              <PlayCircle className="h-4 w-4" /> Send now
            </button>
          )}
          {["Scheduled", "Running"].includes(c.status) && canExecute && (
            <button onClick={() => act(() => campaignService.pause(id), "Paused")} disabled={busy}
              className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-60" data-testid="campaign-pause-btn">
              <Pause className="h-4 w-4" /> Pause
            </button>
          )}
          {!["Completed", "Cancelled"].includes(c.status) && canExecute && (
            <button onClick={() => act(() => campaignService.cancel(id), "Cancelled")} disabled={busy}
              className="inline-flex items-center gap-1.5 border border-border text-muted-foreground rounded-lg px-3 py-2 text-sm hover:bg-muted disabled:opacity-60" data-testid="campaign-cancel-btn">
              <Ban className="h-4 w-4" /> Cancel
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-border mb-6">
        {[["build", "Build"], ["log", "Execution Log"], ["analytics", "Analytics"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`campaign-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "build" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Template (governed)" icon={FileText} testid="campaign-template-section">
            <select value={c.templateId || ""} disabled={!editable || !canEdit}
              onChange={(e) => saveField({ templateId: e.target.value || null })} className="ciq-input" data-testid="campaign-template-select">
              <option value="">Select a template…</option>
              {validTemplates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.channelType})</option>)}
            </select>
            {c.channel === "whatsapp" && (
              <>
                <p className="text-xs text-muted-foreground mt-2">Only WhatsApp-approved templates are selectable.</p>
                <label className="block text-xs font-medium text-muted-foreground mt-3 mb-1">WhatsApp sending channel</label>
                <select value={c.channelId || ""} disabled={!editable || !canEdit}
                  onChange={(e) => saveField({ channelId: e.target.value || null })} className="ciq-input" data-testid="campaign-channel-id">
                  <option value="">Select channel…</option>
                  {channels.map((ch) => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
                </select>
              </>
            )}
            {c.channel !== "whatsapp" && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {c.channel.toUpperCase()} delivery is simulated (placeholder) — live sending arrives with the {c.channel} connector.</p>
            )}
          </Section>

          <Section title="Audience Builder" icon={Users} testid="campaign-audience-section">
            <p className="text-xs text-muted-foreground mb-2">Select segments to target:</p>
            <div className="max-h-44 overflow-y-auto space-y-1 mb-3">
              {segments.length === 0 && <p className="text-sm text-muted-foreground">No segments available</p>}
              {segments.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`campaign-segment-${s.id}`}>
                  <input type="checkbox" checked={(c.segmentIds || []).includes(s.id)} disabled={!editable || !canEdit}
                    onChange={() => toggleSegment(s.id)} />
                  <span className="text-foreground">{s.name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{s.memberCount ?? 0} members</span>
                </label>
              ))}
            </div>
            <button onClick={refreshAudience} disabled={busy} className="text-xs text-primary hover:underline mb-3" data-testid="campaign-refresh-audience">Refresh consent preview</button>
            {audience && (
              <div className="rounded-lg border border-border p-3 bg-muted/30" data-testid="campaign-audience-preview">
                <div className="flex items-center justify-around text-center mb-2">
                  <div><p className="text-lg font-bold text-foreground">{audience.total}</p><p className="text-xs text-muted-foreground">Targeted</p></div>
                  <div><p className="text-lg font-bold text-emerald-600 flex items-center gap-1"><ShieldCheck className="h-4 w-4" />{audience.eligible}</p><p className="text-xs text-muted-foreground">Consent-eligible</p></div>
                  <div><p className="text-lg font-bold text-amber-600">{audience.excluded}</p><p className="text-xs text-muted-foreground">Excluded</p></div>
                </div>
                {Object.keys(audience.exclusionReasons || {}).length > 0 && (
                  <div className="text-xs text-muted-foreground border-t border-border pt-2">
                    {Object.entries(audience.exclusionReasons).map(([k, v]) => <div key={k}>• {k}: {v}</div>)}
                  </div>
                )}
              </div>
            )}
          </Section>

          {c.status === "Approved" && canExecute && (
            <Section title="Schedule" icon={CalendarClock} testid="campaign-schedule-section">
              <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="ciq-input mb-2" data-testid="campaign-schedule-input" />
              <button onClick={() => scheduleAt && act(() => campaignService.schedule(id, { scheduledAt: new Date(scheduleAt).toISOString() }), "Scheduled")}
                disabled={!scheduleAt || busy} className="w-full bg-violet-600 text-white rounded-lg px-3 py-2 text-sm font-semibold hover:bg-violet-700 disabled:opacity-60" data-testid="campaign-schedule-btn">
                Schedule send
              </button>
            </Section>
          )}

          <Section title="Approval status" icon={ShieldCheck} testid="campaign-approval-section">
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge color={CAMPAIGN_STATUS_COLOR[c.status]}>{c.status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Submitted</span><span className="text-foreground">{c.approval?.submittedAt ? c.approval.submittedAt.slice(0, 16).replace("T", " ") : "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Approved</span><span className="text-foreground">{c.approval?.approvedAt ? c.approval.approvedAt.slice(0, 16).replace("T", " ") : "—"}</span></div>
            </div>
          </Section>
        </div>
      )}

      {tab === "log" && (
        <Section title="Execution Log & Response Tracking" testid="campaign-log-section">
          {responses.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No sends yet — execute the campaign to see results.</p> : (
            <div className="space-y-1.5">
              {responses.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm border-b border-border py-2" data-testid={`campaign-response-${r.id}`}>
                  <span className="text-foreground">{r.customerName}</span>
                  <span className="text-xs text-muted-foreground">{r.recipient}</span>
                  <Badge color={RESPONSE_COLOR[r.status] || "slate"}>{r.status}{r.simulated ? " (sim)" : ""}</Badge>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === "analytics" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="Delivery metrics" testid="campaign-analytics-section">
            {analytics ? (
              <div className="grid grid-cols-2 gap-3">
                {[["Sent", analytics.stats.sent], ["Delivered", analytics.stats.delivered], ["Read", analytics.stats.read], ["Failed", analytics.stats.failed], ["Replied", analytics.stats.replied], ["Opted out", analytics.stats.optedOut]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">{k}</p><p className="text-xl font-bold text-foreground">{v ?? 0}</p></div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground py-6 text-center">Execute the campaign to see analytics.</p>}
          </Section>
          {analytics && (
            <Section title="Rates" testid="campaign-rates-section">
              <div className="space-y-3">
                {[["Delivery rate", analytics.deliveryRate], ["Read rate", analytics.readRate], ["Response rate", analytics.responseRate]].map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">{k}</span><span className="font-semibold text-foreground">{v}%</span></div>
                    <div className="bg-muted rounded-full h-2"><div className="bg-primary h-2 rounded-full" style={{ width: `${Math.min(100, v)}%` }} /></div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
